import React, { useEffect, useMemo, useState } from 'react';
import '../App.css';
import { Storage } from 'aws-amplify';

const S3_KEY = 'nfl/daily.json';          // <-- change to your JSON object's key
const S3_LEVEL = 'public';           // 'public' | 'protected' | 'private'

// If your JSON doesn’t include "season_start", set it here (local time OK)
// Example: NFL 2026 regular season start (first Thu after Labor Day): 2026-09-09T00:00:00Z
const FALLBACK_SEASON_START_ISO = '2026-09-09T00:00:00Z';

function Test() {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState('all'); // 'all' | number (stringified in <select>)
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // -------- load from S3 ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await Storage.get(S3_KEY, { level: S3_LEVEL, download: true });
        const blob = res?.Body instanceof Blob ? res.Body : res;
        const text = await blob.text();
        const parsed = JSON.parse(text);
        if (!cancelled) setData(parsed);
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // --------------------------------

  const seasonStartISO = useMemo(
    () => data?.season_start || FALLBACK_SEASON_START_ISO,
    [data]
  );

  // Derive week if missing, else use provided
  const getWeek = (g) => {
    if (g.week != null) return Number(g.week);
    if (!g.kickoff_iso) return undefined;
    const kickoff = new Date(g.kickoff_iso);
    const start = new Date(seasonStartISO);
    const diffDays = Math.floor((kickoff - start) / 86400000); // ms/day
    return diffDays >= 0 ? Math.floor(diffDays / 7) + 1 : undefined;
  };

  const gamesWithWeek = useMemo(() => {
    if (!data?.games) return [];
    return data.games.map(g => ({ ...g, _week: getWeek(g) }));
  }, [data, seasonStartISO]);

  const availableWeeks = useMemo(() => {
    const s = new Set();
    gamesWithWeek.forEach(g => { if (g._week != null) s.add(g._week); });
    return Array.from(s).sort((a, b) => a - b);
  }, [gamesWithWeek]);

  // ---------- NEW: auto-select week based on "two days before" rule ----------
  useEffect(() => {
    if (week !== 'all' || availableWeeks.length === 0) return;

    const start = new Date(seasonStartISO).getTime();
    const TWO_DAYS = 2 * 86400000;
    const now = Date.now();

    // Build windows: [wkStart-2d, nextWkStart-2d)
    // wkStart = seasonStart + (w-1)*7d
    const sevenDays = 7 * 86400000;

    // Find the week whose window contains "now"
    let chosen = null;
    for (let i = 0; i < availableWeeks.length; i++) {
      const w = availableWeeks[i];
      const wkStart = start + (w - 1) * sevenDays;
      const nextW = availableWeeks[i + 1] ?? (w + 1); // assume contiguous after last
      const nextStart = start + (nextW - 1) * sevenDays;

      const windowStart = wkStart - TWO_DAYS;
      const windowEnd = nextStart - TWO_DAYS;

      if (now >= windowStart && now < windowEnd) {
        chosen = w;
        break;
      }
    }

    // Fallbacks: before first window → first week; after last → last week
    if (chosen == null) {
      const firstW = availableWeeks[0];
      const lastW = availableWeeks[availableWeeks.length - 1];
      const firstStartMinus2 = start + (firstW - 1) * sevenDays - TWO_DAYS;
      const lastNextStartMinus2 = start + (lastW) * sevenDays - TWO_DAYS; // window end of last if contiguous
      chosen = (now < firstStartMinus2) ? firstW
              : (now >= lastNextStartMinus2) ? lastW
              : firstW; // safe default
    }

    setWeek(String(chosen));
  }, [availableWeeks, seasonStartISO, week]);
  // --------------------------------------------------------------------------

  const filtered = useMemo(() => {
    if (week === 'all') return gamesWithWeek;
    const target = Number(week);
    return gamesWithWeek.filter(g => g._week === target);
  }, [gamesWithWeek, week]);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="content">
      {loading && <p>Loading JSON from S3…</p>}
      {error && <p style={{ color: 'crimson' }}>Error: {error}</p>}

      {data && (
        <>
          <h2 style={{ marginBottom: 8 }}>
            Generated at: {data.generated_at ? formatDate(data.generated_at) : '—'}
          </h2>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '8px 0 16px' }}>
            <label>
              <span style={{ marginRight: 8, fontWeight: 600 }}>Week:</span>
              <select value={week} onChange={e => setWeek(e.target.value)}>
                <option value="all">All</option>
                {availableWeeks.map(w => (
                  <option key={w} value={String(w)}>Week {w}</option>
                ))}
              </select>
            </label>
            <small style={{ opacity: 0.7 }}>
              Lines supplied by DraftKings
            </small>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 640, width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f7f7f7' }}>
                  <th style={th}>Week</th>
                  <th style={th}>Kickoff</th>
                  <th style={th}>Home Team</th>
                  <th style={th}>Spread Home Team</th>
                  <th style={th}>Away Team</th>
                  <th style={th}>Spread Away Team</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((g, i) => (
                  <tr key={i} style={i % 2 ? rowAlt : row}>
                    <td style={tdCenter}>{g._week ?? '—'}</td>
                    <td style={td}>{g.kickoff_iso ? formatDate(g.kickoff_iso) : '—'}</td>
                    <td style={{ ...td, fontWeight: 600, color: '#09168aff' }}>{g.team_home || '—'}</td>
                    <td style={{ ...tdCenter, fontVariantNumeric: 'tabular-nums' }}>{g.spread_home ?? '—'}</td>
                    <td style={{ ...td, fontWeight: 600, color: '#09168aff' }}>{g.team_away || '—'}</td>
                    <td style={{ ...tdCenter, fontVariantNumeric: 'tabular-nums' }}>{g.spread_away ?? '—'}</td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td style={{ ...td, padding: 16 }} colSpan={5}>No games for this selection.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// table styles
const th = { textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #ddd' };
const td = { padding: '8px', borderBottom: '1px solid #eee' };
const tdCenter = { ...td, textAlign: 'center' };
const row = {};
const rowAlt = { background: '#fafafa' };

export default Test;